import geometry from '../../../../scene/util/geometry';
import productService, {
  isType,
  isSection,
} from '../../../../services/products';
import constants from '../../../../settings/constants';
import { unique } from '../../../../util/array';
import tacHelpers from '../../tacHelpers';
import { getDependencyDiff } from './dependentItems';
import { ITEMS } from '../../../../constants';
import store from '../../..';
import _ from 'lodash';

const { SECTION_SIDE_UNITS, SECTION_POSTS, SHOE_SHELF } = ITEMS;

const INSERT_MIN_DISTANCE_FROM_TOP = 70;

/**
 * Removes slots that should be closed in the extendable part of posts sections.
 * @param {Array} slots An array of slots describing the holes available to hang the component on it
 * @returns
 */
function filterSlotsExtendablePart(slots) {
  return slots.filter(slot => {
    if (!isType(slot.parent, ITEMS.SECTION_POSTS) || isType(slot, ITEMS.POST))
      return true;

    const sectionHeightCurrent = slot.parent.height;
    const sectionHeightUnextended = slot.parent.filter.height;
    const itemsInExtendablePart = slot.parent.items.filter(
      item => item.y > sectionHeightUnextended
    );
    const otherItemsInExtendablePart = itemsInExtendablePart.filter(
      item => item.itemid !== slot.itemid
    );

    return otherItemsInExtendablePart.length
      ? slot.y + slot.height <=
          sectionHeightUnextended - INSERT_MIN_DISTANCE_FROM_TOP
      : slot.y + slot.height <=
          sectionHeightCurrent - INSERT_MIN_DISTANCE_FROM_TOP;
  });
}

/**
 * A function which will be called when dropping an ELVARLI component in the planner
 *
 * @param {Array} slots An array of slots describing the holes available to hang the component on it
 * @returns {Boolean}
 */
function filterSlots(slots, tac) {
  const elvarliVariant = store.getState().rangeData.elvarliVariant;
  const slotsRemaining =
    elvarliVariant === ITEMS.SECTION_POSTS
      ? filterSlotsExtendablePart(slots)
      : slots;

  return slotsRemaining.filter(slot => {
    if (isType(slot, ITEMS.SHELF_DRAWER)) {
      const SHELF_DRAWER_MAX_Y = 1389;
      return slot.y + slot.height <= SHELF_DRAWER_MAX_Y;
    } else if (isType(slot, SHOE_SHELF)) {
      const SHOE_SHELF_MAX_Y = 2160;
      return slot.y + slot.height <= SHOE_SHELF_MAX_Y;
    } else if (
      isType(slot, ITEMS.SHELF_CLOTHES_RAIL) &&
      isType(slot.parent, ITEMS.SECTION_SIDE_UNITS)
    ) {
      const SHELF_CLOTHES_RAIL_MIN_Y = 900;
      return slot.y + slot.height >= SHELF_CLOTHES_RAIL_MIN_Y;
    }
    return slot => slot.y + slot.height <= slot.parent.height + slot.parent.y;
  });
}

function filterVariants(tac, variants) {
  const usableVariants = variants.filter(
    variant => !productService.hasMissingBrackets(variant)
  );

  return usableVariants;
}

function findSuperSection(tac, member, skiplist) {
  if (!tacHelpers.isWithinWall(member, tac)) {
    return [member];
  }

  const superSection = [member];

  const candidates = tac.items.filter(
    item =>
      item.itemid !== member.itemid &&
      skiplist.indexOf(item.itemid) === -1 &&
      tacHelpers.isWithinWall(item, tac)
  );

  const fullSizeMember = tacHelpers.getFullSize(member);

  const collisions = candidates.filter(candidate =>
    geometry.collides(fullSizeMember, tacHelpers.getFullSize(candidate))
  );
  const skip = skiplist
    .concat(collisions.map(item => item.itemid))
    .filter(unique);

  const rest = collisions.map(collision =>
    findSuperSection(tac, collision, skip)
  );

  return Array.prototype.concat.apply(superSection, rest).filter(unique);
}

function getWallResizingLimits(tac) {
  const tacAdjusted = _.cloneDeep(tac);
  const sectionsPosts =
    tacAdjusted.items?.filter(item => isType(item, ITEMS.SECTION_POSTS)) || [];
  sectionsPosts.forEach(sectionPosts => {
    const inserts =
      sectionPosts.items?.filter(item => !isType(item, ITEMS.POST)) || [];
    const highestPositionReachedByInsert = inserts.reduce((highest, insert) => {
      return Math.max(highest, insert.y + insert.height);
    }, 0);
    sectionPosts.height = Math.max(
      highestPositionReachedByInsert + INSERT_MIN_DISTANCE_FROM_TOP,
      constants.WALL.height.min
    );
  });
  const limits = tacHelpers.getLimits(tacAdjusted);
  return limits;
}

/**
 * Checks whether a section has an obstructing item placed
 * above the space in-between its posts
 *
 * @param {Object} section The section to check if blocked
 * @param {Object} overlapper The other section asking to overlap
 * @param {Array} nonSections All top-level items in the TAC that are not sections
 * @param {Object} tac The entire TAC
 * @returns {Boolean} If there is a blocking item or not
 */
function isBlockedAboveCenter(section, overlapper, nonSections, tac) {
  const postWidth = productService.getPostWidth();
  const spaceBetweenAfterMerge = {
    ...section,
    x: section.x + postWidth,
    z: 0,
    width: section.width - postWidth * 2,
    height: Math.max(overlapper.height, section.height),
  };

  if (
    nonSections.some(item =>
      geometry.contains(spaceBetweenAfterMerge, { ...item, height: 1 })
    )
  ) {
    // Something is placed inside the area above the relative
    const superSection = findSuperSection(tac, section, [overlapper.itemid]);
    if (
      superSection.find(
        item =>
          isSection(item) &&
          item.height > section.height &&
          (item.x < section.x || item.x > section.x)
      )
    ) {
      // Our relative has a higher neighbour on at least one side,
      // meaning that the area above would become a section area post-merge
      return true;
    }
  }
}

/**
 * Checks the conditions for potential merge on respective side of the relative
 *
 * @param {Object} item The section asking for what overlap is allowed
 * @param {Object} relative The other section we might want to overlap
 * @param {Object} tac The entire TAC
 * @returns {Object} An object with boolean properties "left" and "right" stating whether overlap is allowed
 */
function getBlockedDirections(item, relative, tac) {
  const postWidth = productService.getPostWidth();
  const blockedDirections = {
    left: false,
    right: false,
  };

  const nonSections = tac.items.filter(item => !isSection(item));

  if (isBlockedAboveCenter(relative, item, nonSections, tac)) {
    blockedDirections.left = true;
    blockedDirections.right = true;
  }

  // Either side could also be blocked if there is something placed above the actual post
  if (!blockedDirections.left) {
    const leftPostAfterMerge = {
      ...relative,
      width: postWidth,
      height: Math.max(item.height, relative.height),
    };

    const itemAfterMergeLeft = {
      ...item,
      x: leftPostAfterMerge.x - item.width,
      y: leftPostAfterMerge.y,
      z: leftPostAfterMerge.z,
    };

    blockedDirections.left =
      geometry.getCollidingRects(leftPostAfterMerge, nonSections).length ||
      isBlockedAboveCenter(itemAfterMergeLeft, relative, nonSections, tac);
  }

  if (!blockedDirections.right) {
    const rightPostAfterMerge = {
      ...relative,
      x: relative.x + relative.width - postWidth,
      width: postWidth,
      height: Math.max(item.height, relative.height),
    };

    const itemAfterMergeRight = {
      ...item,
      x: rightPostAfterMerge.x,
      y: rightPostAfterMerge.y,
      z: rightPostAfterMerge.z,
    };

    blockedDirections.right =
      geometry.getCollidingRects(rightPostAfterMerge, nonSections).length ||
      isBlockedAboveCenter(itemAfterMergeRight, relative, nonSections, tac);
  }

  return blockedDirections;
}

/**
 * Get the allowed overlap for an item to on another item (the relative)
 *
 * @param {Object} tac The entire TAC
 * @param {Object} item The item asking for what overlap is allowed
 * @param {Object} relative The other item we might want to overlap
 * @returns {Object} An object with the dimensions of the allowed overlap
 */
function getAllowedOverlap(tac, item, relative) {
  let x = 0;
  let width = 0;
  const postWidth = productService.getPostWidth();

  const sameDepthSectionRelation =
    item.depth === relative.depth && [item, relative].every(isSection);

  if (sameDepthSectionRelation) {
    const blockedDirections = getBlockedDirections(item, relative, tac);

    x = !blockedDirections.left ? postWidth : 0;
    width =
      -postWidth *
      (blockedDirections.left && blockedDirections.right
        ? 0
        : !blockedDirections.left && !blockedDirections.right
        ? 2
        : 1);
  }

  return { x, y: 0, z: 0, width, height: 0, depth: 0 };
}

/**
 * Get the "snapped" position of a dragged item, if close enough to a suitable partner
 *
 * @param {Object} item The item asking to snap
 * @param {Array} rects All other rects on the scene
 * @returns {Object | undefined} An object with new coordinates for the snapped position,
 * or undefined if no suitable partner found
 */
function getSnappingPosition(item, rects) {
  if (!isSection(item)) {
    return;
  }

  const postWidth = productService.getPostWidth();
  const sectionRects = rects.filter(isSection);
  const closeRects = geometry.getCollidingRects(item, sectionRects, {
    right: constants.SECTION_SNAPPING_DISTANCE + postWidth - 1,
    left: constants.SECTION_SNAPPING_DISTANCE + postWidth - 1,
  });

  const partner = geometry.closestCollidingRect(item, closeRects);

  if (!partner?.overlap || partner.depth !== item.depth) {
    return;
  }

  const { overlap } = partner;

  if (partner.x < item.x && overlap.width + overlap.x < 0) {
    return { x: partner.x + partner.width };
  } else if (partner.x > item.x && overlap.x > 0) {
    return { x: partner.x - item.width };
  }
}

/**
 * Checks whether an incoming item is allowed to ignore the spacing bounds of
 * an already present item.
 *
 * @param {Object} candidate The item asking to ignore its spacing bounds
 * @param {Object} relative The other item in the relation
 * @param {Object} [tac] The entire TAC
 * @returns {Boolean} True/false depending on if ignoring is allowed,
 */
function shouldIgnoreSpacingBounds(candidate, relative = null, tac = null) {
  return false;
}

function bothAreCrossBraces(item, candidate) {
  return (
    isType(item, ITEMS.CROSS_BRACE) && isType(candidate, ITEMS.CROSS_BRACE)
  );
}

function itemIsPostBase(item) {
  return item.modelid.includes('post') && item.modelid.includes('_base');
}

function itemIsPostTop(item) {
  return item.modelid.includes('post') && item.modelid.includes('_top');
}

function itemIsDrawer(item) {
  return isType(item, ITEMS.DRAWER);
}

function itemIsShelf(item) {
  return isType(item, ITEMS.SHELF);
}

const oneOfIsShelf = (item, candidate) => {
  return isType(item, ITEMS.SHELF) || isType(candidate, ITEMS.SHELF);
};

function itemIsShelfDrawer(item) {
  return isType(item, ITEMS.SHELF_DRAWER);
}

const shelfDrawerShouldIgnoreShelfDrawer = (drawer, drawerSibling, parent) => {
  if (drawer.y === drawerSibling.y) {
    return false;
  }
  return true;
};

const oneOfIsSideUnit = (item, candidate) => {
  return (
    isType(item, ITEMS.SECTION_SIDE_UNITS) ||
    isType(candidate, ITEMS.SECTION_SIDE_UNITS)
  );
};

const oneOfIsPosts = (item, candidate) => {
  return (
    isType(item, ITEMS.SECTION_POSTS) || isType(candidate, ITEMS.SECTION_POSTS)
  );
};

const oneItemIsBracket = (item, candidate) => {
  return isType(item, ITEMS.BRACKET) || isType(candidate, ITEMS.BRACKET);
};

function shouldIgnoreCollision(item, parent, candidate) {
  if (bothAreCrossBraces(item, candidate)) return true;
  if (
    (itemIsPostBase(item) && itemIsPostTop(candidate)) ||
    (itemIsPostBase(candidate) && itemIsPostTop(item))
  )
    return true;

  if (itemIsDrawer(candidate) && itemIsShelf(item) && itemIsShelfDrawer(parent))
    return true;
  if (itemIsDrawer(item) && itemIsShelf(candidate) && itemIsShelfDrawer(parent))
    return true;

  if (oneOfIsSideUnit(item, candidate) && oneOfIsShelf(item, candidate))
    return true;

  if (oneOfIsPosts(item, candidate) && oneOfIsShelf(item, candidate))
    return true;

  if (oneItemIsBracket(item, candidate)) return true;
  if (
    [item, candidate].every(item =>
      productService.isType(item, ITEMS.SHELF_DRAWER)
    )
  ) {
    return shelfDrawerShouldIgnoreShelfDrawer(item, candidate, parent);
  }

  return (
    isType(parent, [SECTION_SIDE_UNITS, SECTION_POSTS]) &&
    isType(candidate, [SECTION_SIDE_UNITS, SECTION_POSTS]) &&
    geometry.collides(parent, candidate)
  );
}

/**
 * Gets all the other items in the TAC that are dependent of a specific item
 * @param {*} item
 * @param {*} tac
 * @returns {Array}
 */
const getDependentItems = (item, tac) => {
  return [];
};

export default {
  filterSlots,
  filterVariants,
  findSuperSection,
  getAllowedOverlap,
  getDependencyDiff,
  getSnappingPosition,
  shouldIgnoreCollision,
  shouldIgnoreSpacingBounds,
  getDependentItems,
  getWallResizingLimits,
};
